import { getCustomLanguageFromURL } from '@/utils/customLanguageUtils';
import { SUPPORTED_LANGUAGES } from '@/utils/constants';
import api from '@/utils/ky';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  CheckCircle,
  FileText,
  Headphones,
  Lightbulb,
  MessageSquare,
  Pause,
  Pencil,
  RefreshCw,
  ThumbsUp,
  TrendingUp,
  XCircle,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import FilePdf from '../../../components/icons/FilePdf';

interface FeedbackAnalysisProps {
  feedback: {
    frameworkAnalysis: {
      steps: Array<{
        name: string;
        assessment: {
          score: number;
          examples: string[];
          improvement: string;
        };
      }>;
      overallScore: number;
    };
    strengthsAndAreas: {
      strengths: string[];
      areasForImprovement: string[];
    };
    conversationHighlights: {
      bestPractices: string[];
      missedOpportunities: string[];
    };
    competitorKnowledge?: {
      positives: string[];
      negatives: string[];
      overallAssessment: string;
      missedOpportunities: string[];
    };
    actionableNextSteps: Array<{
      action: string;
      description: string;
    }>;
    productKnowledge: {
      strengths: any;
      gaps: any;
      accuracyScore?: number;
      productDetails?: {
        name: string;
        type: string;
      };
      analysis?: any;
    };
  };
  framework: any;
  onPracticeAgain: () => void;
  sessionId: string;
  hasCompetitorData?: boolean;
  scenarioCode?: string;
}

export function FeedbackAnalysis({
  feedback,
  framework,
  onPracticeAgain,
  sessionId,
  hasCompetitorData = false,
  scenarioCode,
}: FeedbackAnalysisProps) {
  const { t, i18n } = useTranslation();
  const currentLanguage = getCustomLanguageFromURL();
  const isAxaHkScenario = scenarioCode?.startsWith('axa-hk-');
  const maxStepScore = isAxaHkScenario ? 25 : 10;
  const [isTtsLoading, setIsTtsLoading] = useState(false);
  const [isTtsPlaying, setIsTtsPlaying] = useState(false);
  const [isTtsGenerating, setIsTtsGenerating] = useState(false);
  const [ttsError, setTtsError] = useState<string | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const ttsSocketRef = useRef<WebSocket | null>(null);
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);
  const ttsFeedbackRef = useRef<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioPlayPromiseRef = useRef<Promise<void> | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  const [cachedAudioData, setCachedAudioData] = useState<string | null>(null);
  const cachedAudioRef = useRef<string | null>(null);
  const [isInitialLoadPeriod, setIsInitialLoadPeriod] = useState(true);

  // PDF stuff
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const MAX_RECONNECT_ATTEMPTS = 5;

  // Helper function to get TTS language code from current language
  const getTTSLanguageCode = (langCode: string): string => {
    const language = SUPPORTED_LANGUAGES.find(lang => lang.code === langCode);
    return language?.TTS || 'en-US';
  };

  // Set i18n language when component mounts or language changes
  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  // Add this to your existing useEffect that runs on component mount
  useEffect(() => {
    // Set a timeout to enable the button after 5 seconds
    const initialLoadTimer = setTimeout(() => {
      setIsInitialLoadPeriod(false);
    }, 5000);

    // Cleanup the timer on unmount
    return () => clearTimeout(initialLoadTimer);
  }, []);

  // Initialize TTS WebSocket with reconnection logic
  const connectToTTS = useCallback(() => {
    // Clear any existing connections
    if (ttsSocketRef.current) {
      ttsSocketRef.current.close();
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    try {
      setIsReconnecting(true);
      const ttsSocket = new WebSocket(
        import.meta.env.VITE_TTS_WEBSOCKET_URI || 'ws://localhost:5002',
      );
      ttsSocketRef.current = ttsSocket;

      ttsSocket.onopen = () => {
        console.log('TTS WebSocket connected for feedback');
        setIsReconnecting(false);
        setReconnectAttempts(0);
        setTtsError(null);

        // Initialize with basic message handler
        resetWebSocketHandler();

        // Don't automatically send/play feedback on reconnect
        // The user must click the button to play
      };

      // onmessage handler will be set by resetWebSocketHandler in onopen

      ttsSocket.onerror = (error) => {
        console.log('TTS WebSocket error:', error);
        handleSocketDisconnect('Connection error');
      };

      ttsSocket.onclose = (event) => {
        console.log('TTS WebSocket closed:', event);
        if (!event.wasClean) {
          handleSocketDisconnect('Connection closed unexpectedly');
        } else {
          setIsReconnecting(false);
        }
      };
    } catch (error) {
      console.log('Error creating TTS WebSocket:', error);
      handleSocketDisconnect('Failed to create connection');
    }
  }, []);

  // Handle WebSocket disconnection with backoff retry
  const handleSocketDisconnect = useCallback(
    (errorMsg: string) => {
      setIsReconnecting(true);
      if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        const backoffTime = Math.pow(2, reconnectAttempts) * 1000;
        console.log(
          `Reconnecting to TTS in ${backoffTime}ms (attempt ${
            reconnectAttempts + 1
          }/${MAX_RECONNECT_ATTEMPTS})`,
        );

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }

        reconnectTimeoutRef.current = setTimeout(() => {
          setReconnectAttempts((prev) => prev + 1);
          connectToTTS();
        }, backoffTime);
      } else {
        setIsReconnecting(false);
        setTtsError(`${errorMsg}. Max reconnection attempts reached.`);
        setIsTtsLoading(false);
        setIsTtsGenerating(false);
      }
    },
    [reconnectAttempts, connectToTTS],
  );

  // Initialize connection on mount
  useEffect(() => {
    connectToTTS();

    return () => {
      cleanupAudioResources();

      if (ttsSocketRef.current) {
        ttsSocketRef.current.close();
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Enhanced useEffect to prefetch and preload TTS audio
  useEffect(() => {
    // Start generating TTS feedback and preload audio as soon as the feedback component loads
    const preFetchTtsFeedback = async () => {
      try {
        // First check if TTS feedback already exists
        const response: any = await api
          .get(
            `api/custom/sessions/${sessionId}/tts-feedback?language=${currentLanguage}`,
            {
              timeout: 5000,
              retry: 1,
            },
          )
          .json();

        if (response.ttsFeedback) {
          // Store the text feedback
          ttsFeedbackRef.current = response.ttsFeedback;

          // Preload the audio if WebSocket is connected
          if (ttsSocketRef.current?.readyState === WebSocket.OPEN) {
            console.log('Preloading TTS audio in chunks...');

            // Split the text into manageable chunks for preloading
            const textChunks = splitTextIntoChunks(response.ttsFeedback);

            // Reset audio chunks collection
            audioChunksRef.current = [];
            currentChunkIndexRef.current = 0;

            // Set up handler for the audio data (preloading)
            const originalOnMessage = ttsSocketRef.current.onmessage;
            ttsSocketRef.current.onmessage = (message) => {
              try {
                const data = JSON.parse(message.data);
                if (data.audioContent && data.preloadOnly) {
                  // Store the audio chunk
                  audioChunksRef.current.push(data.audioContent);
                  currentChunkIndexRef.current++;

                  console.log(
                    `Preloaded chunk ${currentChunkIndexRef.current}/${textChunks.length}`,
                  );

                  // If we've received all chunks, combine and cache them
                  if (currentChunkIndexRef.current >= textChunks.length) {
                    console.log('All chunks preloaded successfully');
                    const combinedAudio = audioChunksRef.current.join('');
                    // Cache the combined audio
                    cachedAudioRef.current = combinedAudio;
                    setCachedAudioData(combinedAudio);
                    console.log('Audio cached, NOT playing - waiting for user action');
                    // Restore original handler or reset to basic handler
                    resetWebSocketHandler();
                  } else {
                    // Request the next chunk
                    ttsSocketRef.current?.send(
                      JSON.stringify({
                        text: textChunks[currentChunkIndexRef.current],
                        languageCode: getTTSLanguageCode(currentLanguage),
                        preloadOnly: true,
                        isChunk: true,
                        chunkIndex: currentChunkIndexRef.current,
                      }),
                    );
                  }
                } else if (!data.preloadOnly && originalOnMessage) {
                  // Delegate to original handler for non-preload messages
                  originalOnMessage.call(ttsSocketRef.current, message);
                }
              } catch (error) {
                console.log(
                  'Error processing preloaded TTS audio chunk:',
                  error,
                );
                resetWebSocketHandler();
              }
            };

            // Start by requesting the first chunk
            ttsSocketRef.current.send(
              JSON.stringify({
                text: textChunks[0],
                languageCode: getTTSLanguageCode(currentLanguage),
                preloadOnly: true,
                isChunk: true,
                chunkIndex: 0,
                totalChunks: textChunks.length,
              }),
            );
          }
        } else {
          // If not, start generating it in the background (non-blocking)
          const generateResponse: any = await api
            .post(`api/custom/sessions/${sessionId}/generate-tts-feedback`, {
              json: {
                hasCompetitorData,
                competitorMentioned: hasCompetitorData,
                language: currentLanguage,
              },
              timeout: 60000,
            })
            .json();

          if (generateResponse.ttsFeedback) {
            // Store the generated feedback text
            ttsFeedbackRef.current = generateResponse.ttsFeedback;

            // Preload the audio if WebSocket is connected
            if (ttsSocketRef.current?.readyState === WebSocket.OPEN) {
              console.log('Preloading TTS audio in chunks after generation...');

              // Split the text into manageable chunks for preloading
              const textChunks = splitTextIntoChunks(
                generateResponse.ttsFeedback,
              );

              // Reset audio chunks collection
              audioChunksRef.current = [];
              currentChunkIndexRef.current = 0;

              // Set up handler for the audio data (preloading after generation)
              const originalOnMessage2 = ttsSocketRef.current.onmessage;
              ttsSocketRef.current.onmessage = (message) => {
                try {
                  const data = JSON.parse(message.data);
                  if (data.audioContent && data.preloadOnly) {
                    // Store the audio chunk
                    audioChunksRef.current.push(data.audioContent);
                    currentChunkIndexRef.current++;

                    console.log(
                      `Preloaded chunk ${currentChunkIndexRef.current}/${textChunks.length}`,
                    );

                    // If we've received all chunks, combine and cache them
                    if (currentChunkIndexRef.current >= textChunks.length) {
                      console.log(
                        'All chunks preloaded successfully after generation',
                      );
                      const combinedAudio = audioChunksRef.current.join('');
                      // Cache the combined audio
                      cachedAudioRef.current = combinedAudio;
                      setCachedAudioData(combinedAudio);
                      console.log('Audio cached after generation, NOT playing - waiting for user action');
                      // Restore original handler or reset to basic handler
                      resetWebSocketHandler();
                    } else {
                      // Request the next chunk
                      ttsSocketRef.current?.send(
                        JSON.stringify({
                          text: textChunks[currentChunkIndexRef.current],
                          languageCode: getTTSLanguageCode(currentLanguage),
                          preloadOnly: true,
                          isChunk: true,
                          chunkIndex: currentChunkIndexRef.current,
                        }),
                      );
                    }
                  } else if (!data.preloadOnly && originalOnMessage2) {
                    // Delegate to original handler for non-preload messages
                    originalOnMessage2.call(ttsSocketRef.current, message);
                  }
                } catch (error) {
                  console.log(
                    'Error processing preloaded TTS audio chunk:',
                    error,
                  );
                  resetWebSocketHandler();
                }
              };

              // Start by requesting the first chunk
              ttsSocketRef.current.send(
                JSON.stringify({
                  text: textChunks[0],
                  languageCode: getTTSLanguageCode(currentLanguage),
                  preloadOnly: true,
                  isChunk: true,
                  chunkIndex: 0,
                  totalChunks: textChunks.length,
                }),
              );
            }
          }
        }
      } catch (err) {
        // Non-critical error, can be ignored
        console.log('Error pre-fetching TTS feedback:', err);
      }
    };

    // Only attempt to pre-fetch if we have a sessionId
    if (sessionId) {
      preFetchTtsFeedback();
    }

    // PDF stuff
    const preFetchPdf = async () => {
      try {
        // First check if PDF already exists
        const response: any = await api
          .get(
            `api/custom/sessions/${sessionId}/pdf?language=${currentLanguage}`,
            {
              timeout: 5000,
            },
          )
          .json();

        if (response.pdfUrl) {
          console.log('PDF already exists');
          setPdfUrl(response.pdfUrl);
        } else {
          // Start generating PDF in background
          console.log('Prefetching PDF generation');
          setIsPdfGenerating(true);

          api
            .post(`api/custom/sessions/${sessionId}/generate-pdf`, {
              json: {
                userName: 'Sales Professional', // Default name
                language: currentLanguage,
              },
              timeout: 60000,
            })
            .json()
            .then((data: any) => {
              console.log('PDF generated successfully');
              setPdfUrl(data.pdfUrl);
            })
            .catch((err) => {
              console.log('Error generating PDF:', err);
            })
            .finally(() => {
              setIsPdfGenerating(false);
            });
        }
      } catch (err) {
        console.log('Error pre-fetching PDF status:', err);
      }
    };

    // Only attempt to pre-fetch if we have a sessionId
    if (sessionId) {
      preFetchTtsFeedback();
      preFetchPdf();
    }

    // Cleanup function to reset the WebSocket onmessage handler
    return () => {
      if (ttsSocketRef.current) {
        resetWebSocketHandler();
      }
    };
  }, [sessionId, hasCompetitorData]);

  // Clean up audio resources
  const cleanupAudioResources = useCallback((clearCache = true) => {
    console.log(
      'Cleaning up audio resources',
      clearCache ? '(including cache)' : '(keeping cache)',
    );
    // Stop any current audio
    if (ttsAudioRef.current) {
      ttsAudioRef.current.onplay = null;
      ttsAudioRef.current.onpause = null;
      ttsAudioRef.current.onended = null;
      ttsAudioRef.current.onerror = null;

      try {
        if (ttsAudioRef.current) {
          ttsAudioRef.current.pause();
        }
      } catch (e) {
        console.log('Error pausing audio:', e);
      }

      ttsAudioRef.current = null;
    }

    // Clean up any created object URLs
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }

    // Clear cache if requested
    if (clearCache) {
      cachedAudioRef.current = null;
      setCachedAudioData(null);
    }

    audioPlayPromiseRef.current = null;
    setIsTtsPlaying(false);
  }, []);

  // Function to handle base64 to audio blob conversion and playback
  const playAudio = useCallback(
    (base64Audio: string) => {
      try {
        // Clean up existing audio resources first
        cleanupAudioResources();

        // Cache the audio data if not already cached
        if (!cachedAudioRef.current) {
          cachedAudioRef.current = base64Audio;
          setCachedAudioData(base64Audio);
        }

        const byteCharacters = atob(base64Audio);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const audioBlob = new Blob([byteArray], { type: 'audio/mp3' });
        const url = URL.createObjectURL(audioBlob);
        audioUrlRef.current = url;

        const audio = new Audio(url);
        ttsAudioRef.current = audio;

        // Add a safety timeout to prevent UI getting stuck
        const safetyTimeoutId = setTimeout(() => {
          // If we're still loading after 5 seconds, there's likely a problem
          if (isTtsLoading) {
            console.log('Audio play timeout - forcing state reset');
            setIsTtsLoading(false);
            setTtsError('Audio playback timed out. Please try again.');
          }
        }, 5000);

        // Set up event handlers
        audio.onplay = () => {
          console.log('Audio play started');
          clearTimeout(safetyTimeoutId); // Clear the safety timeout
          setIsTtsPlaying(true);
          setIsTtsLoading(false);
        };

        audio.onpause = () => {
          console.log('Audio paused');
          setIsTtsPlaying(false);
        };

        audio.onended = () => {
          console.log('Audio playback completed');
          setIsTtsPlaying(false);
          cleanupAudioResources(false); // Keep the cached data
        };

        // Ensure audio plays only once by removing the loop attribute
        audio.loop = false;

        audio.onerror = (e) => {
          console.log('Audio playback error:', e);
          clearTimeout(safetyTimeoutId); // Clear the safety timeout
          setTtsError('Failed to play audio');
          setIsTtsPlaying(false);
          setIsTtsLoading(false);
          cleanupAudioResources(false); // Keep the cached data
        };

        // Add canplaythrough event to make sure audio is ready
        audio.oncanplaythrough = () => {
          console.log('Audio can play through without buffering');
        };

        // Start playback and store the promise
        console.log('Starting audio playback');
        audioPlayPromiseRef.current = audio.play();

        // Handle potential promise rejection
        audioPlayPromiseRef.current
          .then(() => {
            console.log('Play promise fulfilled');
          })
          .catch((err) => {
            console.log('Failed to play audio:', err);
            clearTimeout(safetyTimeoutId); // Clear the safety timeout
            // Only set error if the audio element is still the same (hasn't been cleaned up)
            if (ttsAudioRef.current === audio) {
              setTtsError('Failed to play audio: ' + (err.message || err));
              setIsTtsLoading(false);
              setIsTtsPlaying(false);
              cleanupAudioResources(false); // Keep the cached data
            }
          });

        // Make sure we clean up the timeout if component unmounts
        return () => {
          clearTimeout(safetyTimeoutId);
        };
      } catch (error) {
        console.log('Error playing audio:', error);
        setTtsError('Failed to process audio data');
        setIsTtsLoading(false);
        cleanupAudioResources(false); // Keep the cached data
      }
    },
    [cleanupAudioResources, isTtsLoading],
  );

  // Function to stop audio playback
  const stopAudio = useCallback(() => {
    if (!ttsAudioRef.current) return;

    console.log('Stopping audio playback');

    // If there's a pending play promise, we need to handle it to avoid the AbortError
    if (audioPlayPromiseRef.current) {
      audioPlayPromiseRef.current
        .then(() => {
          if (ttsAudioRef.current) {
            ttsAudioRef.current.pause();
            ttsAudioRef.current.currentTime = 0;
            setIsTtsPlaying(false);
          }
        })
        .catch((err) => {
          console.log('Error in play promise when stopping:', err);
          // The play was already aborted, just update the state
          setIsTtsPlaying(false);
        });
    } else {
      // No pending play promise, safe to pause directly
      ttsAudioRef.current.pause();
      ttsAudioRef.current.currentTime = 0;
      setIsTtsPlaying(false);
    }
  }, []);

  // Split long text into manageable chunks for TTS processing
  const splitTextIntoChunks = (
    text: string,
    maxChunkLength: number = 1000,
  ): string[] => {
    // Split at sentence boundaries to keep natural pauses
    const sentences = text.split(/(?<=[.!?])\s+/);
    const chunks: string[] = [];
    let currentChunk = '';

    sentences.forEach((sentence) => {
      // If adding this sentence would exceed the chunk size, start a new chunk
      if (
        currentChunk.length + sentence.length > maxChunkLength &&
        currentChunk.length > 0
      ) {
        chunks.push(currentChunk.trim());
        currentChunk = '';
      }
      currentChunk += sentence + ' ';
    });

    // Add the last chunk if it's not empty
    if (currentChunk.trim().length > 0) {
      chunks.push(currentChunk.trim());
    }

    return chunks;
  };

  // Store all audio chunks for combined playback
  const audioChunksRef = useRef<string[]>([]);
  const currentChunkIndexRef = useRef<number>(0);

  // Function to reset WebSocket message handler to prevent conflicts
  const resetWebSocketHandler = useCallback(() => {
    if (ttsSocketRef.current) {
      // Reset to the basic message handler to prevent conflicts
      ttsSocketRef.current.onmessage = (message) => {
        try {
          const data = JSON.parse(message.data);
          if (data.audioContent && !data.isChunk && !data.preloadOnly) {
            // Only play if user explicitly requested it (button was clicked)
            // Check if we're in a loading state which indicates user action
            if (isTtsLoading && !isTtsPlaying) {
              playAudio(data.audioContent);
            } else {
              console.log('Received audio but not playing - user did not request playback');
            }
          }
        } catch (error) {
          console.log('Error processing TTS message:', error);
          setTtsError('Failed to process audio data');
          setIsTtsLoading(false);
        }
      };
    }
  }, [playAudio, isTtsLoading, isTtsPlaying]);

  // Function to process TTS chunks sequentially
  const processTtsChunks = async (
    chunks: string[],
    languageCode?: string,
  ) => {
    const ttsLanguageCode = languageCode || getTTSLanguageCode(currentLanguage);
    if (
      !ttsSocketRef.current ||
      ttsSocketRef.current.readyState !== WebSocket.OPEN
    ) {
      setTtsError('TTS service not connected. Attempting to reconnect...');
      connectToTTS();
      return;
    }

    // Reset audio chunks
    audioChunksRef.current = [];
    currentChunkIndexRef.current = 0;

    // Set up the onmessage handler to collect chunks
    ttsSocketRef.current.onmessage = (message) => {
      try {
        const data = JSON.parse(message.data);
        if (data.audioContent) {
          // Store the audio chunk
          audioChunksRef.current.push(data.audioContent);
          currentChunkIndexRef.current++;

          // If we've received all chunks, combine and play them
          if (currentChunkIndexRef.current >= chunks.length) {
            const combinedAudio = audioChunksRef.current.join('');
            // Cache the combined audio
            cachedAudioRef.current = combinedAudio;
            setCachedAudioData(combinedAudio);
            // Play the combined audio ONLY ONCE
            playAudio(combinedAudio);
            // Reset handler to prevent further processing
            resetWebSocketHandler();
          } else {
            // Send the next chunk
            ttsSocketRef.current?.send(
              JSON.stringify({
                text: chunks[currentChunkIndexRef.current],
                languageCode: ttsLanguageCode,
                isChunk: true,
                chunkIndex: currentChunkIndexRef.current,
              }),
            );
          }
        }
      } catch (error) {
        console.log('Error processing TTS message:', error);
        setTtsError('Failed to process audio data');
        setIsTtsLoading(false);
        // Reset handler on error
        resetWebSocketHandler();
      }
    };

    // Send the first chunk to start the process
    ttsSocketRef.current.send(
      JSON.stringify({
        text: chunks[0],
        languageCode: ttsLanguageCode,
        isChunk: true,
        chunkIndex: 0,
        totalChunks: chunks.length,
      }),
    );
  };

  // Helper function to get performance level description based on score
  const getPerformanceLevelDescription = (score: number, frameworkStep: any): string | null => {
    if (!frameworkStep?.performanceLevels) {
      return null;
    }

    const lowThreshold = maxStepScore * 0.4;
    const midThreshold = maxStepScore * 0.7;

    if (score >= 0 && score <= lowThreshold) {
      return frameworkStep.performanceLevels.low;
    } else if (score > lowThreshold && score < midThreshold) {
      return frameworkStep.performanceLevels.average;
    } else if (score >= midThreshold) {
      return frameworkStep.performanceLevels.high;
    }

    return null;
  };

  // Function to play feedback via TTS
  const handlePlayFeedback = async () => {
    // If currently playing, stop the audio
    if (isTtsPlaying) {
      stopAudio();
      return;
    }

    try {
      setIsTtsLoading(true);
      setTtsError(null);

      // If we have cached audio data, use it directly
      if (cachedAudioRef.current) {
        console.log('Using cached TTS audio');
        playAudio(cachedAudioRef.current);
        return;
      }

      // Otherwise, try to get existing TTS-friendly feedback
      const response: any = await api
        .get(
          `api/custom/sessions/${sessionId}/tts-feedback?language=${currentLanguage}`,
          {
            timeout: 10000,
            retry: 2,
          },
        )
        .json();

      if (response.ttsFeedback) {
        console.log('Using existing TTS feedback');
        // If feedback exists, process it in chunks
        ttsFeedbackRef.current = response.ttsFeedback;

        if (ttsSocketRef.current?.readyState === WebSocket.OPEN) {
          // Split the text into manageable chunks and process sequentially
          const textChunks = splitTextIntoChunks(response.ttsFeedback);
          processTtsChunks(textChunks);
        } else {
          // Socket not ready, try to reconnect
          setTtsError('TTS service not connected. Attempting to reconnect...');
          connectToTTS();
        }
      } else {
        // If feedback doesn't exist, generate it
        setIsTtsGenerating(true);
        const generateResponse: any = await api
          .post(`api/custom/sessions/${sessionId}/generate-tts-feedback`, {
            json: {
              hasCompetitorData,
              language: currentLanguage,
            },
            timeout: 20000,
            retry: 2,
          })
          .json();
        setIsTtsGenerating(false);

        if (generateResponse.ttsFeedback) {
          ttsFeedbackRef.current = generateResponse.ttsFeedback;

          if (ttsSocketRef.current?.readyState === WebSocket.OPEN) {
            // Split the text into manageable chunks and process sequentially
            const textChunks = splitTextIntoChunks(
              generateResponse.ttsFeedback,
            );
            processTtsChunks(textChunks);
          } else {
            // Socket not ready, try to reconnect
            setTtsError(
              'TTS service not connected. Attempting to reconnect...',
            );
            connectToTTS();
          }
        } else {
          throw new Error('Failed to generate feedback summary');
        }
      }
    } catch (error) {
      console.log('Error handling TTS feedback:', error);
      setTtsError(
        typeof error === 'object' && error !== null && 'message' in error
          ? error.message
          : 'Failed to play feedback',
      );
      setIsTtsLoading(false);
      setIsTtsGenerating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Framework Steps Analysis */}
      <section className="bg-white rounded-xl shadow-sm p-4 lg:p-6">
        <h2 className="text-lg lg:text-xl font-semibold mb-4 flex items-center">
          <TrendingUp className="w-5 lg:w-6 h-5 lg:h-6 mr-2 text-blue-600" />
          {t('custom.roleplay.feedback.frameworkImplementation')}
        </h2>
        <div className="space-y-4">
          {feedback.frameworkAnalysis.steps.map((step, index) => (
            <motion.div
              key={step.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border-b border-gray-100 pb-4"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">{step.name}</h3>
                <div className="flex items-center">
                  <span className="text-sm font-medium mr-2">
                    {step.assessment.score}/{maxStepScore}
                  </span>
                  {step.assessment.score >= maxStepScore * 0.7 ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : step.assessment.score >= maxStepScore * 0.4 ? (
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                {framework.steps[index] && framework.steps[index].description}
              </p>
              {/* Performance Level Description */}
              {(() => {
                const frameworkStep = framework?.steps?.[index];
                const performanceDesc = frameworkStep ? getPerformanceLevelDescription(step.assessment.score, frameworkStep) : null;

                return frameworkStep && performanceDesc ? (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded mt-2 mb-2">
                    <p className="text-sm text-gray-700">
                      {performanceDesc}
                    </p>
                  </div>
                ) : null;
              })()}
              {step.assessment.examples.length > 0 && (
                <div className="bg-gray-50 p-3 rounded-lg mt-2">
                  <p className="text-sm font-medium mb-1">
                    {t('custom.roleplay.feedback.examplesFromConversation')}:
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-600">
                    {step.assessment.examples.map((example, i) => (
                      <li key={i}>{example}</li>
                    ))}
                  </ul>
                </div>
              )}
              {step.assessment.improvement && (
                <div className="mt-2 text-sm text-indigo-600">
                  <strong>
                    {t('custom.roleplay.feedback.improvementTip')}:
                  </strong>{' '}
                  {step.assessment.improvement}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Strengths & Areas for Improvement */}
      <div className="grid grid-cols-2 gap-6">
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <CheckCircle className="w-6 h-6 mr-2 text-green-500" />
            {t('custom.roleplay.feedback.keyStrengths')}
          </h2>
          {feedback.strengthsAndAreas.strengths &&
          feedback.strengthsAndAreas.strengths.length > 0 ? (
            <ul className="space-y-3">
              {feedback.strengthsAndAreas.strengths.map((strength, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start"
                >
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">{strength}</span>
                </motion.li>
              ))}
            </ul>
          ) : (
            <p className="text-xs lg:text-sm text-gray-500">
              {t('custom.roleplay.feedback.noKeyStrengths')}
            </p>
          )}
        </section>

        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <TrendingUp className="w-6 h-6 mr-2 text-yellow-500" />
            {t('custom.roleplay.feedback.areasForImprovement')}
          </h2>
          {feedback.strengthsAndAreas.areasForImprovement &&
          feedback.strengthsAndAreas.areasForImprovement.length > 0 ? (
            <ul className="space-y-3">
              {feedback.strengthsAndAreas.areasForImprovement.map(
                (area, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm">{area}</span>
                  </motion.li>
                ),
              )}
            </ul>
          ) : (
            <p className="text-xs lg:text-sm text-gray-500">
              {t('custom.roleplay.feedback.noAreasForImprovement')}
            </p>
          )}
        </section>
      </div>

      {/* Product Knowledge - Enhanced Version (hidden for claims scenarios where rubric covers it) */}
      {!isAxaHkScenario && (
      <section className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <BookOpen className="w-6 h-6 mr-2 text-purple-600" />
          {t('custom.roleplay.feedback.productKnowledge')}
          {feedback.productKnowledge?.accuracyScore !== undefined && (
            <span
              className="ml-auto text-sm font-normal bg-gray-100 px-3
  py-1 rounded-full"
            >
              {t('custom.roleplay.feedback.accuracyScore')}:{' '}
              {feedback.productKnowledge?.accuracyScore}/10
            </span>
          )}
        </h2>

        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Strengths Section */}
            <div>
              <h3
                className="text-lg font-semibold mb-3 flex
  items-center"
              >
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                {t('custom.roleplay.feedback.strengths')}
              </h3>

              {feedback.productKnowledge.strengths &&
              feedback.productKnowledge.strengths.length > 0 ? (
                <div className="space-y-3">
                  {feedback.productKnowledge.strengths.map(
                    (strength, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start bg-green-50 p-3
  rounded-lg"
                      >
                        <div className="mr-3 mt-0.5 flex-shrink-0">
                          {strength.category === 'features' ? (
                            <Zap className="w-4 h-4 text-green-600" />
                          ) : strength.category === 'explanation' ? (
                            <MessageSquare
                              className="w-4 h-4
  text-green-600"
                            />
                          ) : strength.category === 'accuracy' ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <ThumbsUp className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                        <span
                          className="text-sm
  text-gray-700"
                        >
                          {strength.text}
                        </span>
                      </motion.div>
                    ),
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  {t('custom.roleplay.feedback.noProductStrengths')}
                </p>
              )}
            </div>

            {/* Gaps Section */}
            <div>
              <h3
                className="text-lg font-semibold mb-3 flex
  items-center"
              >
                <AlertTriangle className="w-5 h-5 text-amber-500 mr-2" />
                {t('custom.roleplay.feedback.areasForImprovement')}
              </h3>

              {feedback.productKnowledge.gaps &&
              feedback.productKnowledge.gaps.length > 0 ? (
                <div className="space-y-3">
                  {/* Show factual errors first with special styling */}
                  {feedback.productKnowledge.gaps
                    .filter((gap) => gap.isFactualError)
                    .map((gap, index) => (
                      <motion.div
                        key={`factual-${index}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`flex items-start rounded-lg p-3 ${
                          gap.severity === 'high'
                            ? 'bg-red-50 border border-red-100'
                            : gap.severity === 'medium'
                            ? 'bg-amber-50 border border-amber-100'
                            : 'bg-yellow-50'
                        }`}
                      >
                        <div className="mr-3 mt-0.5 flex-shrink-0">
                          {gap.severity === 'high' ? (
                            <XCircle className="w-4 h-4 text-red-600" />
                          ) : (
                            <AlertTriangle
                              className="w-4 h-4
  text-amber-600"
                            />
                          )}
                        </div>

                        <div className="flex-1">
                          {/* Special formatting for summary rows */}
                          {gap.isSummary ? (
                            <p
                              className="text-sm font-medium
  text-red-700"
                            >
                              {gap.text}
                            </p>
                          ) : (
                            <>
                              <p
                                className="text-sm
  text-gray-700"
                              >
                                {gap.text}
                              </p>
                              {/* Add correction tip if available */}
                              {gap.correction && (
                                <div className="mt-1 flex items-center">
                                  <div
                                    className="bg-white px-2 py-1
  rounded text-xs text-emerald-700 flex items-center"
                                  >
                                    <span
                                      className="inline-block
  bg-emerald-100 w-2 h-2 rounded-full mr-1"
                                    ></span>
                                    <span className="font-medium">
                                      {t('custom.roleplay.feedback.correct')}:
                                    </span>
                                    <span className="ml-1">
                                      {gap.correction}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </motion.div>
                    ))}

                  {/* Then show general improvement areas */}
                  {feedback.productKnowledge.gaps
                    .filter((gap) => !gap.isFactualError)
                    .map((gap, index) => (
                      <motion.div
                        key={`general-${index}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className="flex items-start bg-gray-50 p-3
  rounded-lg"
                      >
                        <div className="mr-3 mt-0.5 flex-shrink-0">
                          {gap.category === 'features' ? (
                            <Lightbulb className="w-4 h-4 text-gray-600" />
                          ) : gap.category === 'explanation' ? (
                            <MessageSquare
                              className="w-4 h-4
  text-gray-600"
                            />
                          ) : gap.category === 'details' ? (
                            <FileText className="w-4 h-4 text-gray-600" />
                          ) : (
                            <Pencil className="w-4 h-4 text-gray-600" />
                          )}
                        </div>
                        <span
                          className="text-sm
  text-gray-700"
                        >
                          {gap.text}
                        </span>
                      </motion.div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  {t('custom.roleplay.feedback.noProductKnowledgeGaps')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Show analysis if available */}
        {feedback.productKnowledge.analysis && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-600 italic">
              {feedback.productKnowledge.analysis}
            </p>
          </div>
        )}
      </section>
      )}

      {/* Competitor Knowledge - Only show if competitor data exists */}
      {hasCompetitorData && feedback.competitorKnowledge && (
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <BarChart3 className="w-6 h-6 mr-2 text-purple-600" />
            {t('custom.roleplay.feedback.competitorComparisonHandling')}
          </h2>
          <div className="mb-4">
            <p className="text-sm text-gray-700 mb-3">
              {feedback.competitorKnowledge.overallAssessment}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                {t('custom.roleplay.feedback.effectivePositioning')}
              </h3>
              {feedback.competitorKnowledge.positives.length > 0 ? (
                <ul className="space-y-2">
                  {feedback.competitorKnowledge.positives.map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start"
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('custom.roleplay.feedback.noEffectivePositioning')}
                </p>
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">
                {t('custom.roleplay.feedback.improvementOpportunities')}
              </h3>
              {feedback.competitorKnowledge.negatives.length > 0 ? (
                <ul className="space-y-2">
                  {feedback.competitorKnowledge.negatives.map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start"
                    >
                      <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('custom.roleplay.feedback.noImprovementOpportunities')}
                </p>
              )}
            </div>
          </div>
          {feedback.competitorKnowledge.missedOpportunities &&
            feedback.competitorKnowledge.missedOpportunities.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                <h4 className="text-sm font-medium text-amber-800 mb-2">
                  {t('custom.roleplay.feedback.missedPositioningOpportunities')}
                  :
                </h4>
                <ul className="list-disc list-inside text-sm text-gray-700">
                  {feedback.competitorKnowledge.missedOpportunities.map(
                    (item, i) => (
                      <li key={i}>{item}</li>
                    ),
                  )}
                </ul>
              </div>
            )}
        </section>
      )}

      {/* Conversation Highlights */}
      <section className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <MessageSquare className="w-6 h-6 mr-2 text-indigo-600" />
          {t('custom.roleplay.feedback.conversationHighlights')}
        </h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">
              {t('custom.roleplay.feedback.bestPractices')}
            </h3>
            {feedback.conversationHighlights.bestPractices.length > 0 ? (
              <ul className="list-disc list-inside text-sm text-gray-600">
                {feedback.conversationHighlights.bestPractices.map(
                  (item, index) => (
                    <li key={index}>{item}</li>
                  ),
                )}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">
                {t('custom.roleplay.feedback.noBestPractices')}
              </p>
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">
              {t('custom.roleplay.feedback.missedOpportunities')}
            </h3>
            {feedback.conversationHighlights.missedOpportunities.length > 0 ? (
              <ul className="list-disc list-inside text-sm text-gray-600">
                {feedback.conversationHighlights.missedOpportunities.map(
                  (item, index) => (
                    <li key={index}>{item}</li>
                  ),
                )}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">
                {t('custom.roleplay.feedback.noMissedOpportunities')}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Action Steps */}
      <section className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Zap className="w-6 h-6 mr-2 text-purple-600" />
          {t('custom.roleplay.feedback.nextSteps')}
        </h2>
        <div className="space-y-4">
          {feedback.actionableNextSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-start"
            >
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3">
                <span className="text-sm font-medium text-purple-600">
                  {index + 1}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium">{step?.action}</p>
                <p className="text-sm text-gray-600">{step?.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Practice Again and Listen to Feedback Buttons */}
      <div className="flex flex-wrap justify-center gap-4 md:gap-6 pb-12">
        {/* Listen to Feedback Button */}
        <button
          onClick={handlePlayFeedback}
          disabled={
            (isTtsLoading && !isTtsPlaying) ||
            isReconnecting ||
            isInitialLoadPeriod
          }
          className={`flex items-center justify-center space-x-2 px-5 py-3 rounded-lg shadow-md transition-all duration-200 text-white font-medium min-w-[180px]
  ${
    (isTtsLoading && !isTtsPlaying) || isReconnecting || isInitialLoadPeriod
      ? 'bg-blue-400 cursor-not-allowed opacity-80'
      : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg active:transform active:scale-95'
  }
  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}
        >
          {isInitialLoadPeriod ? (
            <>
              <div className="w-5 h-5 border-t-2 border-r-2 border-white rounded-full animate-spin mr-2" />
              <span>{t('custom.roleplay.feedback.preparingFeedback')}</span>
            </>
          ) : isTtsGenerating ? (
            <>
              <div className="w-5 h-5 border-t-2 border-r-2 border-white rounded-full animate-spin mr-2" />
              <span>{t('custom.roleplay.feedback.preparingFeedback')}</span>
            </>
          ) : isReconnecting ? (
            <>
              <div className="w-5 h-5 border-t-2 border-r-2 border-white rounded-full animate-spin mr-2" />
              <span>{t('custom.roleplay.feedback.reconnecting')}</span>
            </>
          ) : isTtsLoading && !isTtsPlaying ? (
            <>
              <div className="w-5 h-5 border-t-2 border-r-2 border-white rounded-full animate-spin mr-2" />
              <span>{t('custom.roleplay.feedback.loadingAudio')}</span>
            </>
          ) : isTtsPlaying ? (
            <>
              <Pause className="w-5 h-5 mr-2" />
              <span>{t('custom.roleplay.buttons.pauseFeedback')}</span>
            </>
          ) : (
            <>
              <Headphones className="w-5 h-5 mr-2" />
              <span>{t('custom.roleplay.buttons.listenToFeedback')}</span>
            </>
          )}
        </button>

        {/* PDF Download Button */}
        <button
          onClick={() => {
            if (pdfUrl) {
              window.open(pdfUrl, '_blank');
            } else {
              setIsPdfGenerating(true);
              api
                .post(`api/custom/sessions/${sessionId}/generate-pdf`, {
                  json: {
                    userName: 'Sales Professional',
                    language: currentLanguage,
                  },
                  timeout: 60000,
                })
                .json()
                .then((data: any) => {
                  setPdfUrl(data.pdfUrl);
                  window.open(data.pdfUrl, '_blank');
                })
                .catch((err) => {
                  console.error('Error generating PDF:', err);
                  toast.error(
                    t('custom.roleplay.feedback.failedToGeneratePdf'),
                  );
                })
                .finally(() => {
                  setIsPdfGenerating(false);
                });
            }
          }}
          disabled={isPdfGenerating}
          className={`flex items-center justify-center space-x-2 px-5 py-3 rounded-lg shadow-md transition-all duration-200 text-white font-medium min-w-[180px]
      ${
        isPdfGenerating
          ? 'bg-green-400 cursor-not-allowed opacity-80'
          : 'bg-green-600 hover:bg-green-700 hover:shadow-lg active:transform active:scale-95'
      }
      focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50`}
        >
          {isPdfGenerating ? (
            <>
              <div className="w-5 h-5 border-t-2 border-r-2 border-white rounded-full animate-spin mr-2" />
              <span>{t('custom.roleplay.feedback.generatingPdf')}</span>
            </>
          ) : (
            <>
              <FilePdf className="w-5 h-5 mr-2" />
              <span>
                {pdfUrl
                  ? t('custom.roleplay.buttons.downloadPdf')
                  : t('custom.roleplay.buttons.generatePdf')}
              </span>
            </>
          )}
        </button>

        {/* Practice Again Button */}
        <button
          onClick={onPracticeAgain}
          className="flex items-center justify-center space-x-2 px-5 py-3 rounded-lg shadow-md transition-all duration-200 bg-primary text-white font-medium min-w-[180px] hover:bg-primary-dark hover:shadow-lg active:transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
        >
          <RefreshCw className="w-5 h-5 mr-2" />
          <span>{t('custom.roleplay.buttons.practiceAgain')}</span>
        </button>
      </div>

      {ttsError && (
        <div className="flex justify-center">
          <p className="text-red-500 text-sm mb-8 bg-white/90 p-2 rounded-md shadow-sm max-w-md">
            {ttsError}
          </p>
        </div>
      )}
    </div>
  );
}
